import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Package,
  Heart,
  LogOut,
  Sparkles,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Gift,
  Copy,
  Check,
  ShieldCheck,
  CreditCard,
  Clock,
  X,
  Plus,
} from "lucide-react";

import serumImg from "../assests/faceimg.webp"; // Path verify kar lein

interface UserData {
  name: string;
  email: string;
  memberSince?: string;
}

interface AddressData {
  street: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserData | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // Address Slide-over State
  const [isAddressDrawerOpen, setIsAddressDrawerOpen] = useState<boolean>(false);
  const [savedAddress, setSavedAddress] = useState<AddressData>({
    street: "Sector 62",
    city: "Noida",
    state: "Uttar Pradesh",
    pincode: "201301",
    phone: "+91 9876543210",
  });

  const [addressForm, setAddressForm] = useState<AddressData>(savedAddress);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!token || !storedUser) {
      navigate("/");
    } else {
      setUser(JSON.parse(storedUser));
    }
  }, [navigate]);

  const handleLogout = (): void => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/";
  };

  const handleCopyCode = (): void => {
    navigator.clipboard.writeText("CELIORA20");
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSaveAddress = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setSavedAddress(addressForm);
    setIsAddressDrawerOpen(false);
  };

  if (!user) return null;

  return (
    <>
      <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-12 px-4 sm:px-6 lg:px-8 flex justify-center">
        <div className="w-full max-w-7xl space-y-8">
          
          {/* VIP User Profile Header */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-[#E8E2D9] flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div className="flex items-center gap-5 z-10">
              <div className="w-20 h-20 rounded-full bg-[#F7F4EF] border-2 border-[#9D7D4F]/30 flex items-center justify-center text-[#9D7D4F] font-serif text-3xl font-bold shadow-inner">
                {user.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-serif font-semibold text-[#1F1B18]">{user.name}</h2>
                  <span className="inline-flex items-center gap-1 bg-[#FAF8F5] text-[#9D7D4F] border border-[#E8E2D9] text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full">
                    <Sparkles size={11} /> VIP Member
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                  <ShieldCheck size={13} className="text-[#9D7D4F]" /> Member since {user.memberSince || "Aug 2026"}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-5 py-2.5 border border-[#E8E2D9] rounded-xl text-xs font-semibold text-gray-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition"
            >
              <LogOut size={14} /> Log Out
            </button>
          </div>

          {/* Exclusive 20% Discount Banner */}
          <div className="relative rounded-3xl bg-gradient-to-r from-[#1F1B18] via-[#2A2420] to-[#1F1B18] text-white p-6 sm:p-8 shadow-xl overflow-hidden border border-[#9D7D4F]/30">
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-2 max-w-lg">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[2px] text-[#9D7D4F] bg-[#9D7D4F]/10 border border-[#9D7D4F]/30 px-3 py-1 rounded-full">
                  <Gift size={12} /> VIP Benefit
                </span>
                <h3 className="text-2xl sm:text-3xl font-serif tracking-wide text-white">
                  Get Extra 20% Off Your Next Order
                </h3>
                <p className="text-xs text-gray-300 font-light">
                  Use your exclusive member coupon code at checkout on all Seoul-inspired skincare essentials.
                </p>
              </div>

              <div className="w-full md:w-auto bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl flex flex-col sm:flex-row items-center gap-3">
                <div className="text-center sm:text-left px-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-300 block">Promo Code</span>
                  <span className="text-lg font-mono font-bold tracking-wider text-[#9D7D4F]">CELIORA20</span>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="w-full sm:w-auto bg-[#9D7D4F] hover:bg-[#85683F] text-white px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition flex items-center justify-center gap-2 shadow-md"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied!" : "Copy Code"}
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Box 1: My Orders */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm flex flex-col justify-between hover:shadow-md transition">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#9D7D4F] flex items-center gap-1.5"><Package size={15} /> My Orders</span>
                  <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 size={10} /> Delivered</span>
                </div>
                <div className="flex items-center gap-3 bg-[#FAF8F5] p-3 rounded-xl border border-[#E8E2D9]/60">
                  <img src={serumImg} alt="Order" className="w-10 h-10 object-cover rounded-lg bg-[#F7F4EF]" />
                  <div>
                    <h4 className="text-xs font-semibold text-[#1F1B18]">Glow Niacinamide Serum</h4>
                    <p className="text-[11px] text-gray-500">$48.00</p>
                  </div>
                </div>
              </div>
              <Link to="/orders" className="text-xs font-semibold text-[#1F1B18] hover:text-[#9D7D4F] flex items-center gap-1 mt-5 transition">View Order History <ArrowRight size={12} /></Link>
            </div>

            {/* Box 2: Wishlist */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm flex flex-col justify-between hover:shadow-md transition">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#9D7D4F] flex items-center gap-1.5"><Heart size={15} /> My Wishlist</span>
                  <span className="text-xs font-semibold text-gray-400">3 Saved</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">Saved luxury serums, barrier creams & essences.</p>
              </div>
              <Link to="/wishlist" className="text-xs font-semibold text-[#1F1B18] hover:text-[#9D7D4F] flex items-center gap-1 mt-5 transition">View Saved Items <ArrowRight size={12} /></Link>
            </div>

            {/* Box 3: Cards */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm flex flex-col justify-between hover:shadow-md transition">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#9D7D4F] flex items-center gap-1.5"><CreditCard size={15} /> My Cards</span>
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-mono">HDFC •••• 4021</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">Manage saved credit/debit cards for faster checkout.</p>
              </div>
              <button className="text-xs font-semibold text-[#1F1B18] hover:text-[#9D7D4F] text-left mt-5 transition">Manage Payment Methods →</button>
            </div>

            {/* Box 4: Dynamic Address Display & Drawer Trigger */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm flex flex-col justify-between hover:shadow-md transition">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#9D7D4F] flex items-center gap-1.5"><MapPin size={15} /> Primary Address</span>
                </div>
                <p className="text-xs text-[#1F1B18] font-semibold">{savedAddress.street}, {savedAddress.city}</p>
                <p className="text-xs text-gray-500 mt-0.5">{savedAddress.state}, {savedAddress.pincode}</p>
                <p className="text-[11px] text-gray-400 mt-1">{savedAddress.phone}</p>
              </div>
              <button
                onClick={() => {
                  setAddressForm(savedAddress);
                  setIsAddressDrawerOpen(true);
                }}
                className="text-xs font-semibold text-[#1F1B18] hover:text-[#9D7D4F] text-left mt-5 transition"
              >
                Manage Addresses →
              </button>
            </div>

          </div>

          {/* Bottom Routine Section */}
          <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#FAF8F5] rounded-xl text-[#9D7D4F]"><Clock size={20} /></div>
              <div>
                <h4 className="text-xs font-semibold text-[#1F1B18] uppercase tracking-wider">Skincare Consultation Status</h4>
                <p className="text-xs text-gray-500 mt-0.5">Routine ID: #CEL-88402 • Hydration & Barrier Care Routine</p>
              </div>
            </div>
            <Link to="/shop" className="text-xs bg-[#FAF8F5] hover:bg-[#9D7D4F] hover:text-white px-4 py-2 rounded-xl border border-[#E8E2D9] transition font-medium">Retake Quiz</Link>
          </div>

        </div>
      </div>

      {/* ========================================= */}
      {/* ADDRESS SLIDE-OVER DRAWER FORM            */}
      {/* ========================================= */}
      <div
        onClick={() => setIsAddressDrawerOpen(false)}
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] transition-opacity duration-300 ${
          isAddressDrawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[100] shadow-2xl transition-transform duration-300 ease-in-out flex flex-col justify-between ${
          isAddressDrawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div>
          <div className="p-6 border-b border-[#E8E2D9] flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold block">Shipping Details</span>
              <h3 className="font-serif text-xl font-semibold text-[#1F1B18]">Manage Address</h3>
            </div>
            <button
              onClick={() => setIsAddressDrawerOpen(false)}
              className="p-1 text-gray-400 hover:text-black rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          <form id="addressForm" onSubmit={handleSaveAddress} className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                Street / Area / House No.
              </label>
              <input
                type="text"
                required
                value={addressForm.street}
                onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                  City
                </label>
                <input
                  type="text"
                  required
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  required
                  value={addressForm.pincode}
                  onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                  className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                State
              </label>
              <input
                type="text"
                required
                value={addressForm.state}
                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                required
                value={addressForm.phone}
                onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
              />
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-[#E8E2D9] bg-[#FAF8F5]">
          <button
            type="submit"
            form="addressForm"
            className="w-full bg-[#1F1B18] hover:bg-[#9D7D4F] text-white py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition flex items-center justify-center gap-2 shadow-md"
          >
            <Check size={14} />
            <span>Save Address</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Profile;